"use client";

import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Gallery() {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.25
  });
  const images = [{
    src: "https://mdsa.org.mt/wp-content/uploads/2026/07/WhatsApp-Image-2026-05-08-at-12.39.55.jpeg?v=5y7kwmcu3cq",
    alt: "MDSA Annual Conference Presentation",
    caption: "Dr. Jane Smith presenting at the MDSA Annual Conference on advanced endodontic techniques"
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/07/WhatsApp-Image-2026-05-08-at-12.38.40.jpeg?v=2p1ys15n2pn",
    alt: "Board of Administrators Meeting",
    caption: "Board of Administrators quarterly meeting discussing association initiatives"
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/07/WhatsApp-Image-2026-05-08-at-23.45.46.jpeg?v=c980p6fu9g",
    alt: "Continuing Education Workshop",
    caption: "Members participating in a hands‑on continuing education workshop on periodontal procedures"
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/07/WhatsApp-Image-2026-05-08-at-23.45.50.jpeg?v=pmfhfga9wfs",
    alt: "Community Dental Outreach",
    caption: "MDSA community outreach event providing free dental screenings to local residents"
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/07/WhatsApp-Image-2026-05-09-at-10.40.29.jpeg?v=i0tfd2fqk8i",
    alt: "Networking Reception",
    caption: "Annual networking reception bringing together dental specialists from across Malta"
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/07/WhatsApp-Image-2026-05-08-at-23.45.49.jpeg?v=094nzj7yckw3",
    alt: "Clinical Skills Seminar",
    caption: "MDSA members attending a clinical skills seminar on modern restorative dentistry techniques"
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/07/WhatsApp-Image-2026-05-08-at-23.45.481.jpeg?v=tr5v8ptxvnj",
    alt: "Award Ceremony",
    caption: "Annual awards ceremony recognizing outstanding contributions to dental excellence in Malta"
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/07/WhatsApp-Image-2026-05-08-at-23.45.47.jpeg?v=i7dmzqgfeh",
    alt: "Research Collaboration Session",
    caption: "Collaborative research session focusing on innovative approaches to oral health care"
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/04/20260413_223546.jpg?v=jhusx8keiig",
    alt: "Member Registration Drive",
    caption: "MDSA member registration drive welcoming new dental specialists to the association"
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/04/20260413_194923-scaled.jpg?v=5fhbqzjmrnn",
    alt: "International Dental Conference",
    caption: "MDSA delegation representing Malta at the international dental specialists conference"
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-14-at-07.31.41.jpeg?v=xc0ph35xhhb",
    alt: "Dental Specialist Workshop",
    caption: "Specialized workshop focusing on the latest advancements in implantology and surgical techniques."
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/04/20260413_215029-rotated.jpg?v=j6w0toui88p",
    alt: "Board Strategy Session",
    caption: "The Board of Administrators discussing the association's long-term strategic vision for dental excellence."
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/02/WhatsApp-Image-2025-05-21-at-11.45.33.jpeg?v=ahfwm19t1cf",
    alt: "Public Health Initiative",
    caption: "MDSA members volunteering for a national oral health awareness campaign and public screening."
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/02/WhatsApp-Image-2025-05-21-at-11.45.34-2.jpeg?v=fqzlnkhwflc",
    alt: "Annual Gala Dinner",
    caption: "Celebrating professional milestones and community achievements at the MDSA Annual Gala Dinner."
  }, {
    src: "https://mdsa.org.mt/wp-content/uploads/2026/02/WhatsApp-Image-2025-05-21-at-11.45.33-1.jpeg?v=zkxfkrbxj0l",
    alt: "Student Mentorship Program",
    caption: "Senior dental specialists providing guidance to students during our annual career development day."
  }];
  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
  };
  const closeLightbox = () => {
    setSelectedImageIndex(null);
  };
  const navigateImage = (direction: "prev" | "next") => {
    if (selectedImageIndex === null) return;
    if (direction === "prev") {
      setSelectedImageIndex((selectedImageIndex - 1 + images.length) % images.length);
    } else {
      setSelectedImageIndex((selectedImageIndex + 1) % images.length);
    }
  };
  return <section id="gallery" className="text-foreground py-16 !bg-[hsl(215,16%,47%)] sm:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold mb-4 !text-[hsl(210,40%,96%)] sm:text-4xl lg:text-5xl">
              Gallery
            </h2>
            <p className="text-lg max-w-2xl mx-auto !text-[hsl(210,40%,96%)]">
              Explore moments from our meetings and educational events
            </p>
          </div>

          <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image, i) => <div key={i} data-index={i} className={`group relative overflow-hidden rounded-lg cursor-pointer transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:scale-[1.04] hover:shadow-strong ${isVisible ? "animate-fade-in-up" : "opacity-0"}`} style={{
            animationDelay: isVisible ? `${i * 80}ms` : "0ms"
          }} onClick={() => openLightbox(i)}>
                <div className="aspect-[4/3] relative">
                  <img src={image.src} alt={image.alt} className="w-full h-full object-cover transition-transform duration-[var(--duration-slow)] ease-[var(--ease-smooth)] group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-[var(--duration-medium)]" />
                </div>
              </div>)}
          </div>
        </div>
      </div>

      <Dialog open={selectedImageIndex !== null} onOpenChange={open => !open && closeLightbox()}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-dark-background border-border overflow-hidden data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 duration-[250ms]" showCloseButton={false}>
          {selectedImageIndex !== null && <div className="relative flex flex-col">
              <DialogClose className="absolute top-4 right-4 z-10 rounded-sm bg-black/50 p-2 text-white hover:bg-black/70 transition-colors duration-[var(--duration-fast)] focus:outline-none focus:ring-2 focus:ring-ring">
                <X className="size-5" />
                <span className="sr-only">Close</span>
              </DialogClose>

              <div className="relative bg-black">
                <img src={images[selectedImageIndex].src} alt={images[selectedImageIndex].alt} className="w-full h-auto max-h-[70vh] object-contain" />

                <button onClick={e => {
              e.stopPropagation();
              navigateImage("prev");
            }} className="absolute top-1/2 left-4 -translate-y-1/2 rounded-sm bg-black/50 p-2 text-white hover:bg-black/70 transition-colors duration-[var(--duration-fast)] focus:outline-none focus:ring-2 focus:ring-ring" aria-label="Previous image">
                  <ChevronLeft className="size-6" />
                </button>

                <button onClick={e => {
              e.stopPropagation();
              navigateImage("next");
            }} className="absolute top-1/2 right-4 -translate-y-1/2 rounded-sm bg-black/50 p-2 text-white hover:bg-black/70 transition-colors duration-[var(--duration-fast)] focus:outline-none focus:ring-2 focus:ring-ring" aria-label="Next image">
                  <ChevronRight className="size-6" />
                </button>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}